import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Flashcard from './Notecards';
import './FlashcardStudyApp.css';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const FlashcardStudyApp = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [deckSize, setDeckSize] = useState(5);
  const [listOfTopics, setListOfTopics] = useState([]);
  const [listOfNotecards, setListOfNotecards] = useState([]);
  const [listOfCourses, setListOfCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [profileStats, setProfileStats] = useState(null);
  const [studySessionId, setStudySessionId] = useState(null);
  const [sessionStats, setSessionStats] = useState({ cardsStudied: 0, correctAnswers: 0, newCards: 0, reviewCards: 0 });
  const [xpGained, setXpGained] = useState(0);
  const [showXpAnimation, setShowXpAnimation] = useState(false);
  const [leveledUp, setLeveledUp] = useState(false);
  const { user } = useSelector((state) => state.userDetails);
  const access_token = useSelector((state) => state.userLogin.userInfo?.access);

  const handleDeckSizeChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0 && value <= listOfNotecards.length) {
      setDeckSize(value);
      setCurrentIndex(0);
    }
  };

  const handleDifficultyRating = async (difficulty) => {
    const currentCard = listOfNotecards[currentIndex];
    if (!currentCard) return;

    try {
      const config = { headers: { Authorization: `Bearer ${access_token}` } };
      const response = await axios.post(
        `https://api.andrewslearning.com/api/review_card/`,
        {
          flashcard_id: currentCard.id,
          difficulty: difficulty
        },
        config
      );

      // Update session stats
      setSessionStats(prev => ({
        ...prev,
        cardsStudied: prev.cardsStudied + 1,
        correctAnswers: difficulty > 1 ? prev.correctAnswers + 1 : prev.correctAnswers,
        newCards: currentCard.card_state === 'new' ? prev.newCards + 1 : prev.newCards,
        reviewCards: currentCard.card_state !== 'new' ? prev.reviewCards + 1 : prev.reviewCards
      }));

      // Show XP animation for correct answers
      if (difficulty > 1) {
        const baseXP = 5;
        const bonusXP = Math.floor(difficulty * 2); // Bonus based on difficulty
        const totalXP = baseXP + bonusXP;
        setXpGained(totalXP);
        setShowXpAnimation(true);
        
        // Check for level up
        if (profileStats) {
          const oldLevel = profileStats.xp_info.current_level;
          await fetchProfileStats(); // Refresh stats
          const newLevel = profileStats?.xp_info?.current_level || oldLevel;
          if (newLevel > oldLevel) {
            setLeveledUp(true);
            setTimeout(() => setLeveledUp(false), 3000);
          }
        }
        
        setTimeout(() => setShowXpAnimation(false), 2000);
      }

      // Move to next card or end session
      if (currentIndex < deckSize - 1) {
        setCurrentIndex((prevIndex) => prevIndex + 1);
        setIsFlipped(false);
      } else {
        // End session
        if (studySessionId) {
          await endStudySession();
        }
        alert(`Study session complete! You studied ${sessionStats.cardsStudied + 1} cards with ${Math.round(((sessionStats.correctAnswers + (difficulty > 1 ? 1 : 0)) / (sessionStats.cardsStudied + 1)) * 100)}% accuracy.`);
        setCurrentIndex(0);
        setIsFlipped(false);
      }
    } catch (error) {
      console.error("Error recording review:", error);
      // Still advance to next card even if API fails
      if (currentIndex < deckSize - 1) {
        setCurrentIndex((prevIndex) => prevIndex + 1);
        setIsFlipped(false);
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < deckSize - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  };

  const handleCourseSelect = (courseId) => {
    setSelectedCourse(courseId);
    fetchTopics(courseId);
  };

  const handleTopicSelect = (topicId) => {
    setSelectedTopics((prev) => {
      return prev.includes(topicId)
        ? prev.filter((id) => id !== topicId)
        : [...prev, topicId];
    });
  };

  const fetchTopics = async (courseId) => {
    try {
      const config = { headers: { Authorization: `Bearer ${access_token}` } };
      const response = await axios.get(
        `https://api.andrewslearning.com/api/list_topics/${courseId}`,
        config
      );
      setListOfTopics(response.data);
    } catch (error) {
      console.error("Error fetching topics:", error);
    }
  };

  const fetchNotecards = async () => {
    setIsLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${access_token}` } };
      const params = new URLSearchParams();
      
      if (selectedCourse) {
        params.append('course_id', selectedCourse);
      }
      if (selectedTopics.length > 0) {
        params.append('topic_ids', selectedTopics.join(','));
      }
      
      const response = await axios.get(
        `https://api.andrewslearning.com/api/due_cards/?${params.toString()}`,
        config
      );
      setListOfNotecards(response.data);
      
      // Start study session when cards are loaded
      if (response.data.length > 0) {
        await startStudySession();
      }
    } catch (error) {
      console.error("Error fetching due cards:", error);
      // Fallback to old API if new one fails
      try {
        const fallbackConfig = { headers: { Authorization: `Bearer ${access_token}` } };
        const ids = selectedTopics.join(',');
        const fallbackResponse = await axios.get(
          `https://api.andrewslearning.com/api/flashcards/${ids}`,
          fallbackConfig
        );
        setListOfNotecards(fallbackResponse.data);
        if (fallbackResponse.data.length > 0) {
          await startStudySession();
        }
      } catch (fallbackError) {
        console.error("Error fetching flashcards (fallback):", fallbackError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfileStats = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${access_token}` } };
      const response = await axios.get(
        'https://api.andrewslearning.com/api/profile_stats/',
        config
      );
      setProfileStats(response.data);
    } catch (error) {
      console.error("Error fetching profile stats:", error);
    }
  };

  const startStudySession = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${access_token}` } };
      const response = await axios.post(
        'https://api.andrewslearning.com/api/start_session/',
        {
          course_id: selectedCourse || null,
          topic_ids: selectedTopics
        },
        config
      );
      setStudySessionId(response.data.session_id);
    } catch (error) {
      console.error("Error starting study session:", error);
    }
  };

  const endStudySession = async () => {
    if (!studySessionId) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${access_token}` } };
      await axios.put(
        `https://api.andrewslearning.com/api/end_session/${studySessionId}/`,
        sessionStats,
        config
      );
      setStudySessionId(null);
      setSessionStats({ cardsStudied: 0, correctAnswers: 0, newCards: 0, reviewCards: 0 });
    } catch (error) {
      console.error("Error ending study session:", error);
    }
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${access_token}` } };
        const response = await axios.get(
          "https://api.andrewslearning.com/api/getcourses/",
          config
        );
        setListOfCourses(response.data);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };
    fetchCourses();
    fetchProfileStats();
  }, [user, access_token]);

  useEffect(() => {
    if (selectedTopics.length > 0 || selectedCourse) {
      fetchNotecards();
    }
  }, [selectedTopics, selectedCourse]);

  // Reset the flip state when the current index changes
  useEffect(() => {
    setIsFlipped(false);
  }, [currentIndex]);

  return (
    <div className="study-app">
      <h1 className="study-title">Study Flashcards</h1>
      
      {/* XP and Level Display */}
      {profileStats && (
        <div className="profile-header">
          <div className="xp-level-display">
            <div className="level-info">
              <span className="level-badge">Level {profileStats.xp_info.current_level}</span>
              <span className="level-name">{profileStats.xp_info.current_level_name}</span>
            </div>
            <div className="xp-info">
              <div className="xp-bar">
                <div 
                  className="xp-fill" 
                  style={{ width: `${profileStats.xp_info.level_progress}%` }}
                ></div>
              </div>
              <span className="xp-text">
                {profileStats.xp_info.current_xp} XP
                {profileStats.xp_info.next_level_xp && (
                  <> / {profileStats.xp_info.next_level_xp} XP to next level</>
                )}
              </span>
            </div>
            <div className="streak-info">
              🔥 {profileStats.stats.streak} day streak
            </div>
          </div>
          
          {/* Session Stats */}
          {studySessionId && (
            <div className="session-stats">
              <h3>Current Session</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-number">{sessionStats.cardsStudied}</span>
                  <span className="stat-label">Cards Studied</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {sessionStats.cardsStudied > 0 ? Math.round((sessionStats.correctAnswers / sessionStats.cardsStudied) * 100) : 0}%
                  </span>
                  <span className="stat-label">Accuracy</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{sessionStats.newCards}</span>
                  <span className="stat-label">New Cards</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{sessionStats.reviewCards}</span>
                  <span className="stat-label">Reviews</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* XP Gain Animation */}
      <AnimatePresence>
        {showXpAnimation && (
          <motion.div
            className="xp-gain-animation"
            initial={{ scale: 0, y: 0, opacity: 0 }}
            animate={{ scale: 1, y: -50, opacity: 1 }}
            exit={{ scale: 0, y: -100, opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            +{xpGained} XP
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Level Up Animation */}
      <AnimatePresence>
        {leveledUp && (
          <motion.div
            className="level-up-animation"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            🎉 LEVEL UP! 🎉
          </motion.div>
        )}
      </AnimatePresence>

      <div className="controls">
        <label>Select Course:</label>
        <select
          className="select-input"
          onChange={(e) => handleCourseSelect(e.target.value)}
          value={selectedCourse}
        >
          <option value="">-- Choose a Course --</option>
          {listOfCourses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.name}
            </option>
          ))}
        </select>
      </div>

      {listOfTopics.length > 0 && (
        <div className="controls">
          <label>Select Topics:</label>
          <div className="topic-list">
            {listOfTopics.map((topic) => (
              <label className="topic-item" key={topic.id}>
                <input
                  type="checkbox"
                  value={topic.id}
                  onChange={() => handleTopicSelect(topic.id)}
                  checked={selectedTopics.includes(topic.id)}
                />
                {topic.title}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="controls">
        <label htmlFor="deck-size">Number of Flashcards to Study:</label>
        <input
          className="number-input"
          type="number"
          id="deck-size"
          min="1"
          max={listOfNotecards.length}
          value={deckSize}
          onChange={handleDeckSizeChange}
        />
      </div>

      {listOfNotecards.length > 0 ? (
        <div className="flashcard-container">
          <div className="card-info">
            <span className={`card-state ${listOfNotecards[currentIndex]?.card_state || 'new'}`}>
              {listOfNotecards[currentIndex]?.card_state || 'new'}
            </span>
            {listOfNotecards[currentIndex]?.success_rate !== undefined && (
              <span className="success-rate">
                Success: {Math.round(listOfNotecards[currentIndex].success_rate)}%
              </span>
            )}
          </div>
          <Flashcard
            question={listOfNotecards[currentIndex]?.question}
            answer={listOfNotecards[currentIndex]?.answer}
            isFlipped={isFlipped} // Pass flip state
            setIsFlipped={setIsFlipped} // Pass state updater
          />
          {isFlipped ? (
            <div className="difficulty-buttons">
              <button 
                className="difficulty-button again" 
                onClick={() => handleDifficultyRating(1)}
                title="Complete blackout, wrong answer"
              >
                Again
              </button>
              <button 
                className="difficulty-button hard" 
                onClick={() => handleDifficultyRating(2)}
                title="Incorrect response, but correct answer seems familiar"
              >
                Hard
              </button>
              <button 
                className="difficulty-button good" 
                onClick={() => handleDifficultyRating(3)}
                title="Correct response, with some hesitation"
              >
                Good
              </button>
              <button 
                className="difficulty-button easy" 
                onClick={() => handleDifficultyRating(4)}
                title="Correct response, answered immediately"
              >
                Easy
              </button>
            </div>
          ) : (
            <div className="flip-instruction">
              <p>Click the card to reveal the answer, then rate your performance</p>
            </div>
          )}
          
          <div className="navigation-buttons">
            <button className="nav-button" onClick={handlePrev} disabled={currentIndex === 0}>
              Previous
            </button>
            <span className="card-counter">
              {currentIndex + 1} / {deckSize}
            </span>
            <button className="nav-button" onClick={handleNext} disabled={currentIndex === deckSize - 1}>
              Next
            </button>
          </div>
        </div>
      ) : (
        <h2 className="placeholder">Please select topics to begin studying.</h2>
      )}
    </div>
  );
};

export default FlashcardStudyApp;
